/**
 * Lets a feature that just raised an announcement tell the bar to go and look.
 *
 * The bar lives in the layout and its poller is armed by an entry it already holds, so an
 * announcement raised by an XHR on an already-rendered page would otherwise reach the user only on
 * their next full page load — the poller cannot discover the first entry, because discovering
 * entries is what the poller is armed to do.
 *
 * A window event rather than shared state: `main.ts` calls `createPinia()` per mount, so islands
 * have no store in common. They do share `window`.
 */
const ANNOUNCEMENTS_CHANGED = 'merit:announcements-changed';

/** Call after a request the server answers by raising an announcement. */
export function notifyAnnouncementsChanged(): void {
    window.dispatchEvent(new CustomEvent(ANNOUNCEMENTS_CHANGED));
}

/** Returns the unsubscribe. */
export function onAnnouncementsChanged(handler: () => void): () => void {
    window.addEventListener(ANNOUNCEMENTS_CHANGED, handler);
    return () => window.removeEventListener(ANNOUNCEMENTS_CHANGED, handler);
}

const ANNOUNCEMENTS_SHOWN = 'merit:announcements-shown';

/**
 * Where the bar is currently pointing, published whenever that changes. A view dismisses what
 * points at it on arrival, but an announcement landing while the user is already standing there
 * has no arrival to hang off — this is how the view learns one is waiting on its own doorstep.
 */
export function notifyAnnouncementsShown(targets: string[]): void {
    window.dispatchEvent(new CustomEvent(ANNOUNCEMENTS_SHOWN, {detail: targets}));
}

/** Returns the unsubscribe. */
export function onAnnouncementsShown(handler: (targets: string[]) => void): () => void {
    const listener = (event: Event) => handler((event as CustomEvent<string[]>).detail || []);
    window.addEventListener(ANNOUNCEMENTS_SHOWN, listener);
    return () => window.removeEventListener(ANNOUNCEMENTS_SHOWN, listener);
}
